(() => {
  const config = window.Cuisinea || { routes: {}, strings: {} };
  const strings = config.strings || {};

  /* ---------- Helpers ---------- */

  const toastEl = () => document.querySelector('[data-toast]');
  let toastTimer;

  function toast(message) {
    const el = toastEl();
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.hidden = true;
    }, 3500);
  }

  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.dataset.count = String(count);
    });
    document.querySelectorAll('[data-cart-label]').forEach((el) => {
      el.setAttribute('aria-label', `Panier, ${count} article${count > 1 ? 's' : ''}`);
    });
  }

  async function refreshCartCount() {
    try {
      const res = await fetch(config.routes.cartJs, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      setCartCount(cart.item_count);
    } catch (e) {
      /* network errors leave the server-rendered count in place */
    }
  }

  function replaceSection(sectionId, html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(`[data-section-root="${sectionId}"]`);
    const current = document.querySelector(`[data-section-root="${sectionId}"]`);
    if (fresh && current) current.replaceWith(fresh);
  }

  function cartSectionIds() {
    return Array.from(document.querySelectorAll('[data-cart-section]')).map((el) => el.dataset.cartSection);
  }

  /* ---------- Drawers ---------- */

  let lastFocused = null;

  function openDrawer(drawer) {
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    const focusTarget = drawer.querySelector('[data-drawer-focus]') || drawer.querySelector('button, a, input');
    if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
  }

  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer) {
        event.preventDefault();
        openDrawer(drawer);
      }
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('.drawer'));
    }
  });

  document.addEventListener('keydown', (event) => {
    const open = document.querySelector('.drawer.is-open');
    if (!open) return;
    if (event.key === 'Escape') {
      closeDrawer(open);
      return;
    }
    if (event.key === 'Tab') {
      const focusables = open.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* ---------- Header ---------- */

  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-search-toggle]');
    if (!toggle) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
    if (!expanded) {
      const input = panel.querySelector('input[type="search"]');
      if (input) input.focus();
    }
  });

  /* ---------- Quantity inputs ---------- */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-qty-step]');
    if (!button) return;
    const wrapper = button.closest('[data-quantity]');
    const input = wrapper && wrapper.querySelector('input');
    if (!input) return;
    const step = Number(button.dataset.qtyStep);
    const min = Number(input.min || 0);
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + step));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Add to cart ---------- */

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form) return;
    if (!window.fetch || config.cartType === 'page') return;

    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    if (errorEl) errorEl.hidden = true;
    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    }

    const body = new FormData(form);
    const sections = cartSectionIds();
    if (sections.length) {
      body.append('sections', sections.join(','));
      body.append('sections_url', window.location.pathname);
    }

    try {
      const res = await fetch(config.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || strings.addError);
      }
      if (data.sections) {
        Object.entries(data.sections).forEach(([id, html]) => replaceSection(id, html));
      }
      await refreshCartCount();
      const drawer = document.getElementById('CartDrawer');
      if (drawer) {
        openDrawer(drawer);
      } else {
        toast(strings.added);
      }
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message || strings.addError;
        errorEl.hidden = false;
      } else {
        toast(error.message || strings.addError);
      }
    } finally {
      if (button) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  });

  /* ---------- Cart line updates (page + drawer) ---------- */

  async function changeLine(line, quantity, container) {
    const sections = cartSectionIds();
    if (container) container.classList.add('is-updating');
    try {
      const res = await fetch(config.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.updateError);
      if (data.sections) {
        Object.entries(data.sections).forEach(([id, html]) => replaceSection(id, html));
      }
      setCartCount(data.item_count);
    } catch (error) {
      toast(error.message || strings.updateError);
      if (container) container.classList.remove('is-updating');
    }
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line-quantity]');
    if (!input || !window.fetch) return;
    const container = input.closest('[data-cart-container]');
    changeLine(Number(input.dataset.lineQuantity), Number(input.value), container);
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-line-remove]');
    if (!remove || !window.fetch) return;
    event.preventDefault();
    const container = remove.closest('[data-cart-container]');
    changeLine(Number(remove.dataset.lineRemove), 0, container);
  });

  /* ---------- Variant picker ---------- */

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const root = select.closest('[data-product]');
    const dataEl = root && root.querySelector('[data-variants-json]');
    if (!dataEl) return;
    const variants = JSON.parse(dataEl.textContent);
    const variant = variants.find((v) => String(v.id) === select.value);
    if (!variant) return;

    root.querySelectorAll('[data-variant-price]').forEach((el) => {
      el.innerHTML = variant.price_html;
    });
    root.querySelectorAll('[data-variant-sku]').forEach((el) => {
      el.textContent = variant.sku || '—';
    });
    root.querySelectorAll('[data-variant-availability]').forEach((el) => {
      el.textContent = variant.availability_label;
      el.classList.toggle('availability--out', !variant.available);
    });
    const button = root.querySelector('[data-add-button]');
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? strings.addToCart : strings.unavailable;
    }
    if (variant.media_id) {
      const slide = root.querySelector(`[data-media-id="${variant.media_id}"]`);
      if (slide) showSlide(root, slide.dataset.index);
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  });

  /* ---------- Gallery ---------- */

  function showSlide(root, index) {
    root.querySelectorAll('.gallery__slide').forEach((slide) => {
      const active = slide.dataset.index === String(index);
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    root.querySelectorAll('.gallery__thumb').forEach((thumb) => {
      thumb.setAttribute('aria-current', String(thumb.dataset.index === String(index)));
    });
  }

  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('.gallery__thumb');
    if (!thumb) return;
    const root = thumb.closest('[data-product]') || document;
    showSlide(root, thumb.dataset.index);
  });

  /* ---------- Collection sorting / filtering ---------- */

  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-autosubmit]');
    if (field && field.form) field.form.requestSubmit ? field.form.requestSubmit() : field.form.submit();
  });

  /* ---------- Cookie preferences (Shopify Customer Privacy API) ---------- */

  function renderConsentStatus() {
    const list = document.querySelector('[data-consent-status]');
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!list || !privacy) return;
    const labels = { true: 'Accepté', false: 'Refusé' };
    const values = {
      analytics: privacy.analyticsProcessingAllowed && privacy.analyticsProcessingAllowed(),
      marketing: privacy.marketingAllowed && privacy.marketingAllowed(),
      preferences: privacy.preferencesProcessingAllowed && privacy.preferencesProcessingAllowed(),
    };
    Object.entries(values).forEach(([key, value]) => {
      const el = list.querySelector(`[data-consent="${key}"]`);
      if (el) el.textContent = labels[String(Boolean(value))];
    });
    list.hidden = false;
  }

  function loadConsentApi() {
    if (!window.Shopify || !window.Shopify.loadFeatures) return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) renderConsentStatus();
    });
  }

  if (document.querySelector('[data-consent-status]')) {
    loadConsentApi();
    document.addEventListener('visitorConsentCollected', renderConsentStatus);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cookie-preferences]');
    if (!button) return;
    event.preventDefault();
    if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
      window.privacyBanner.showPreferences();
    } else {
      const fallback = document.querySelector('[data-cookie-fallback]');
      if (fallback) {
        fallback.hidden = false;
      } else if (button.dataset.fallbackUrl) {
        window.location.href = button.dataset.fallbackUrl;
      }
    }
  });

  /* ---------- Reveal on scroll ---------- */

  const reveals = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window && reveals.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    reveals.forEach((el) => observer.observe(el));
  } else {
    reveals.forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Contact form: native validation messages in French ---------- */

  document.addEventListener(
    'invalid',
    (event) => {
      const field = event.target;
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) return;
      field.setAttribute('aria-invalid', 'true');
      if (field.validity.valueMissing) {
        field.setCustomValidity(field.type === 'checkbox' ? 'Veuillez cocher cette case pour continuer.' : 'Ce champ est obligatoire.');
      } else if (field.validity.typeMismatch && field.type === 'email') {
        field.setCustomValidity('Veuillez saisir une adresse e-mail valide.');
      } else {
        field.setCustomValidity('');
      }
    },
    true
  );

  document.addEventListener('input', (event) => {
    const field = event.target;
    if (field && field.setCustomValidity) {
      field.setCustomValidity('');
      field.removeAttribute('aria-invalid');
    }
  });

  document.addEventListener('change', (event) => {
    const field = event.target;
    if (field && field.type === 'checkbox' && field.setCustomValidity) {
      field.setCustomValidity('');
      field.removeAttribute('aria-invalid');
    }
  });

  window.CuisineaCart = { refreshCartCount, openDrawer, closeDrawer };
})();
